import { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import PropTypes from 'prop-types'
import apiService from '../services/api'

const TaskManager = forwardRef(({ ideaId, onTasksChange, onOrinTaskCountChange, highlightTaskId = null }, ref) => {
  const [tasks, setTasks] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [newTaskTitle, setNewTaskTitle] = useState('')
  const [newTaskDescription, setNewTaskDescription] = useState('')
  const [isCreating, setIsCreating] = useState(false)
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingTaskId, setEditingTaskId] = useState(null)
  const [editTitle, setEditTitle] = useState('')
  const [editDescription, setEditDescription] = useState('')
  const [isUpdating, setIsUpdating] = useState(false)
  const [isDeleting, setIsDeleting] = useState(null)
  const [isToggling, setIsToggling] = useState(null)

  const loadTasks = async () => {
    try {
      setLoading(true)
      const response = await apiService.getTasks(ideaId)
      const loadedTasks = response.tasks || []
      setTasks(loadedTasks)
      setError('')
      
      // Notify parent component of task changes
      if (onTasksChange) {
        const openTaskCount = loadedTasks.filter(task => !task.completed).length
        onTasksChange(openTaskCount)
      }

      // Notify parent of Orin task count
      if (onOrinTaskCountChange) {
        const orinTaskCount = loadedTasks.filter(task =>
          task.source === 'orin' || task.source === 'orin_assessment'
        ).length
        onOrinTaskCountChange(orinTaskCount)
      }
    } catch (err) {
      console.error('Failed to load tasks:', err)
      setError('Failed to load tasks')
    } finally {
      setLoading(false)
    }
  }

  // Check if Orin tasks already exist
  const hasOrinTasks = () => {
    return tasks.some(task => task.source === 'orin' || task.source === 'orin_assessment')
  }

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    addTasksFromOrin: async (orinTasks) => {
      if (!orinTasks || orinTasks.length === 0) return

      // Check if Orin tasks already exist to prevent duplicates
      if (hasOrinTasks()) {
        console.log('Orin tasks already exist, skipping generation')
        return
      }

      try {
        // Create tasks one by one
        for (const taskInfo of orinTasks) {
          const taskData = {
            title: taskInfo.title,
            description: taskInfo.description,
            ideaId: ideaId,
            source: taskInfo.source || 'orin'
          }
          await apiService.createTask(taskData)
        }
        // Reload tasks after all are created
        await loadTasks()
      } catch (err) {
        console.error('Failed to create tasks from Orin:', err)
      }
    },
    hasOrinTasks,
    refresh: loadTasks  // Allow parent to trigger a refresh
  }))

  const createTask = async (e) => {
    e.preventDefault()
    if (!newTaskTitle.trim()) return

    try {
      setIsCreating(true)
      const taskData = {
        title: newTaskTitle.trim(),
        description: newTaskDescription.trim(),
        ideaId: ideaId
      }
      
      await apiService.createTask(taskData)
      setNewTaskTitle('')
      setNewTaskDescription('')
      setShowCreateForm(false)
      await loadTasks()
      setError('')
    } catch (err) {
      console.error('Failed to create task:', err)
      setError('Failed to create task')
    } finally {
      setIsCreating(false)
    }
  }

  const toggleTaskComplete = async (taskId, currentStatus) => {
    try {
      setIsToggling(taskId)
      await apiService.toggleTaskComplete(taskId, !currentStatus)
      await loadTasks()
      setError('')
    } catch (err) {
      console.error('Failed to update task:', err)
      setError('Failed to update task')
    } finally {
      setIsToggling(null)
    }
  }

  const startEditing = (task) => {
    setEditingTaskId(task.task_id)
    setEditTitle(task.title)
    setEditDescription(task.description || '')
  }

  const cancelEditing = () => {
    setEditingTaskId(null)
    setEditTitle('')
    setEditDescription('')
  }

  const updateTask = async (taskId) => {
    if (!editTitle.trim()) return

    try {
      setIsUpdating(true)
      const taskData = {
        title: editTitle.trim(),
        description: editDescription.trim()
      }
      
      await apiService.updateTask(taskId, taskData)
      await loadTasks()
      setEditingTaskId(null)
      setEditTitle('')
      setEditDescription('')
      setError('')
    } catch (err) {
      console.error('Failed to update task:', err)
      setError('Failed to update task. Please try again.')
    } finally {
      setIsUpdating(false)
    }
  }

  const deleteTask = async (taskId) => {
    console.log('🗑️ Delete task clicked for taskId:', taskId)
    
    if (!confirm('Are you sure you want to delete this task?')) {
      console.log('❌ Delete cancelled by user')
      return
    }

    console.log('✅ Delete confirmed, proceeding...')
    try {
      setIsDeleting(taskId)
      console.log('🚀 Calling apiService.deleteTask...')
      await apiService.deleteTask(taskId)
      console.log('✅ Delete API call successful')
      await loadTasks()
      console.log('✅ Tasks reloaded')
      setError('')
    } catch (err) {
      console.error('❌ Failed to delete task:', err)
      setError('Failed to delete task. Please try again.')
    } finally {
      setIsDeleting(null)
    }
  }

  useEffect(() => {
    if (ideaId) {
      loadTasks()
    }
  }, [ideaId])

  const pendingTasks = tasks.filter(task => !task.completed)
  const completedTasks = tasks.filter(task => task.completed)

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center space-x-3">
          <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
          <span className="text-gray-600">Loading tasks...</span>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Tasks Header */}
      <div className="rounded-2xl bg-gradient-to-br from-gray-700/50 to-gray-700/50 shadow-none outline -outline-offset-1 outline-white/10 px-6 py-8 sm:px-8 relative overflow-hidden">
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/40 to-transparent pointer-events-none"></div>
        <div className="absolute top-0 left-0 w-96 h-40 bg-gradient-to-br from-blue-900/20 via-transparent to-transparent rounded-tl-2xl pointer-events-none"></div>
        <div className="absolute bottom-0 right-0 w-64 h-32 bg-gradient-to-tl from-indigo-900/20 via-transparent to-transparent rounded-br-2xl pointer-events-none"></div>
        <div className="relative z-10">
          <div className="flex items-center space-x-3 mb-8">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
              </svg>
            </div>
            <div>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between md:space-x-4">
                <div className="flex-1">
                  <h3 className="font-display text-2xl md:text-3xl font-light tracking-tight text-white">
                    Tasks
                  </h3>
                  <p className="text-gray-300 text-sm">
                    Keep track of action items, research tasks, and next steps for this idea.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 text-sm text-gray-400">
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                {pendingTasks.length} pending
              </span>
              <span className="flex items-center">
                <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                {completedTasks.length} completed
              </span>
            </div>
            <button
              onClick={() => setShowCreateForm(!showCreateForm)}
              className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-6 py-2 rounded-lg text-sm font-semibold shadow-lg hover:shadow-xl transition-all duration-200 transform hover:scale-105 flex items-center justify-center space-x-2 whitespace-nowrap"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>New Task</span>
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center">
            <svg className="w-5 h-5 text-red-400 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-red-800">{error}</span>
          </div>
        </div>
      )}

      {/* Create Task Form */}
      {showCreateForm && (
        <div className="bg-gray-800/50 border border-gray-700 rounded-xl p-6 shadow-sm">
          <form onSubmit={createTask} className="space-y-4">
            <div>
              <label htmlFor="taskTitle" className="block text-sm font-medium text-gray-300 mb-2">
                Task Title *
              </label>
              <input
                id="taskTitle"
                type="text"
                value={newTaskTitle}
                onChange={(e) => setNewTaskTitle(e.target.value)}
                placeholder="e.g., Research competitors, Create prototype, Contact potential users..."
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 text-white placeholder-gray-500"
                required
                disabled={isCreating}
              />
            </div>
            <div>
              <label htmlFor="taskDescription" className="block text-sm font-medium text-gray-300 mb-2">
                Description (optional)
              </label>
              <textarea
                id="taskDescription"
                value={newTaskDescription}
                onChange={(e) => setNewTaskDescription(e.target.value)}
                placeholder="Add more details about this task..."
                rows={3}
                className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 text-white placeholder-gray-500"
                disabled={isCreating}
              />
            </div>
            <div className="flex items-center space-x-3">
              <button
                type="submit"
                disabled={isCreating || !newTaskTitle.trim()}
                className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-colors"
              >
                {isCreating ? 'Creating...' : 'Create Task'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowCreateForm(false)
                  setNewTaskTitle('')
                  setNewTaskDescription('')
                }}
                className="text-gray-400 hover:text-gray-200 px-4 py-2 font-medium transition-colors"
                disabled={isCreating}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tasks List */}
      <div className="space-y-6">
        {/* Pending Tasks */}
        {pendingTasks.length > 0 && (
          <div>
            <h4 className="font-display text-xl font-light tracking-tight text-white mb-4 flex items-center">
              <svg className="w-5 h-5 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              To Do ({pendingTasks.length})
            </h4>
            <div className="space-y-3">
              {pendingTasks.map((task) => (
                <div
                  key={task.task_id}
                  className={'bg-gray-800/50 border border-gray-700 rounded-lg p-4 hover:shadow-md transition-shadow group' + (highlightTaskId === task.task_id ? ' animate-pulse-highlight' : '')}
                >
                  {editingTaskId === task.task_id ? (
                    /* Edit Form */
                    <div className="space-y-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Task Title *
                        </label>
                        <input
                          type="text"
                          value={editTitle}
                          onChange={(e) => setEditTitle(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 text-white"
                          disabled={isUpdating}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">
                          Description
                        </label>
                        <textarea
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          rows={3}
                          className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-900/50 text-white"
                          disabled={isUpdating}
                        />
                      </div>
                      <div className="flex items-center space-x-3">
                        <button
                          onClick={() => updateTask(task.task_id)}
                          disabled={isUpdating || !editTitle.trim()}
                          className="bg-blue-600 hover:bg-blue-700 disabled:bg-blue-800 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                        >
                          {isUpdating ? 'Saving...' : 'Save'}
                        </button>
                        <button
                          onClick={cancelEditing}
                          className="text-gray-400 hover:text-gray-200 px-4 py-2 font-medium transition-colors"
                          disabled={isUpdating}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  ) : (
                    /* Normal View */
                    <div className="flex items-start space-x-3">
                      <button
                        onClick={() => toggleTaskComplete(task.task_id, task.completed)}
                        className="mt-1 w-5 h-5 border-2 border-gray-600 rounded hover:border-blue-400 transition-colors flex items-center justify-center"
                        disabled={isDeleting === task.task_id || isToggling === task.task_id}
                      >
                        {isToggling === task.task_id ? (
                          <svg className="w-3 h-3 animate-spin text-gray-400" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                        ) : null}
                      </button>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <h5 className="font-display font-light tracking-tight text-white">{task.title}</h5>
                          {(task.source === 'orin' || task.source === 'orin_assessment') && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900/50 text-green-400 border border-green-700/50">
                              Generated by Orin
                            </span>
                          )}
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-400 mt-1">{task.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Created {new Date(task.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => startEditing(task)}
                          className="p-1 text-gray-500 hover:text-blue-400 transition-colors"
                          title="Edit task"
                          disabled={isDeleting === task.task_id}
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => deleteTask(task.task_id)}
                          className="p-1 text-gray-500 hover:text-red-400 transition-colors"
                          title="Delete task"
                          disabled={isDeleting === task.task_id}
                        >
                          {isDeleting === task.task_id ? (
                            <div className="w-4 h-4 border-2 border-red-400 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          )}
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Completed Tasks */}
        {completedTasks.length > 0 && (
          <div>
            <h4 className="font-display text-xl font-light tracking-tight text-white mb-4 flex items-center">
              <svg className="w-5 h-5 text-green-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
              Completed ({completedTasks.length})
            </h4>
            <div className="space-y-3">
              {completedTasks.map((task) => (
                <div
                  key={task.task_id}
                  className={'bg-gray-800/30 border border-gray-700 rounded-lg p-4 opacity-75 group' + (highlightTaskId === task.task_id ? ' animate-pulse-highlight' : '')}
                >
                  <div className="flex items-start space-x-3">
                    <button
                      onClick={() => toggleTaskComplete(task.task_id, task.completed)}
                      className="mt-1 w-5 h-5 bg-green-600 border-2 border-green-600 rounded hover:bg-green-700 transition-colors flex items-center justify-center"
                      disabled={isDeleting === task.task_id || isToggling === task.task_id}
                    >
                      {isToggling === task.task_id ? (
                        <svg className="w-3 h-3 animate-spin text-white" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                      ) : (
                        <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      )}
                    </button>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <h5 className="font-display font-light tracking-tight text-gray-400 line-through">{task.title}</h5>
                        {(task.source === 'orin' || task.source === 'orin_assessment') && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-900/30 text-green-500/70 border border-green-700/30">
                            Generated by Orin
                          </span>
                        )}
                      </div>
                      {task.description && (
                        <p className="text-sm text-gray-500 mt-1 line-through">{task.description}</p>
                      )}
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
                        Completed {new Date(task.completed_at || task.updated_at).toLocaleDateString()}
                      </p>
                    </div>
                    <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => deleteTask(task.task_id)}
                        className="p-1 text-gray-400 dark:text-gray-500 hover:text-red-600 dark:hover:text-red-400 transition-colors"
                        title="Delete task"
                        disabled={isDeleting === task.task_id}
                      >
                        {isDeleting === task.task_id ? (
                          <div className="w-4 h-4 border-2 border-red-600 dark:border-red-400 border-t-transparent rounded-full animate-spin"></div>
                        ) : (
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Empty State */}
        {tasks.length === 0 && (
          <div className="text-center py-12">
            <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
            </div>
            <h3 className="text-lg font-medium text-white mb-2">No tasks yet</h3>
            <p className="text-gray-400 mb-4">Create your first task to get started organising this idea.</p>
            <button
              onClick={() => setShowCreateForm(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded-lg font-semibold transition-colors"
            >
              Create First Task
            </button>
          </div>
        )}
      </div>
    </div>
  )
})

TaskManager.displayName = 'TaskManager'

TaskManager.propTypes = {
  ideaId: PropTypes.string.isRequired,
  onTasksChange: PropTypes.func,
  onOrinTaskCountChange: PropTypes.func,
  highlightTaskId: PropTypes.string
}

export default TaskManager